import React, { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../config";
import LoginImage from "../../assets/loginBg.jpg";
import apiService from "../../services/apiService";
import { isCenterAdmin as roleIsCenterAdmin, isStartupFounder as roleIsStartupFounder } from "../../utils/roles";
import { navigateAfterLogin } from "../../utils/postLoginRedirect";
import { toast, ToastContainer } from "react-toastify";
import {
  EnvelopeIcon,
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowLeftIcon,
  UserCircleIcon
} from '@heroicons/react/24/outline';


const Login = () => {
  const navigate = useNavigate();
  const mfaInputRef = useRef(null);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    remember: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  
  // MFA states
  const [showMfaVerification, setShowMfaVerification] = useState(false);
  const [tempToken, setTempToken] = useState("");
  const [mfaCode, setMfaCode] = useState("");

  const { email, password, remember } = formData;
  const persistStartupCenterIds = async (payload = {}) => {
    try {
      const collectedIds = new Set();
      
      const roleName = (payload?.role_name || localStorage.getItem("roleName") || "").toLowerCase();
      
      // CRITICAL: Clear existing incorrect centerIds for center_admin
      if (roleIsCenterAdmin(roleName)) {
        localStorage.removeItem("centerIds");
      }
      
      // CRITICAL FIX: For center_admin, only store current center, not all memberships
      if (roleIsCenterAdmin(roleName)) {
        // Center admin should only see their own center
        if (payload?.center_id) {
          collectedIds.add(payload.center_id);
        }
      } else {
        // For other roles, collect center memberships as before
        if (payload?.center_id) {
          collectedIds.add(payload.center_id);
        }

        if (Array.isArray(payload?.center_memberships)) {
          payload.center_memberships.forEach((membership) => {
            if (membership?.center_id) {
              collectedIds.add(membership.center_id);
            }
          });
        }
      }
 
      if (roleIsStartupFounder(roleName)) {
        const userId = apiService.getCurrentUserId();
        if (userId) {
          try {
            const query = `/startups?created_by_user_id=${encodeURIComponent(userId)}&pageSize=200&page=1`;
            const startupResponse = await apiService.makeRequest(query);
            const startups = Array.isArray(startupResponse?.data)
              ? startupResponse.data
              : Array.isArray(startupResponse?.results)
              ? startupResponse.results
              : Array.isArray(startupResponse?.startups)
              ? startupResponse.startups
              : [];
 
            startups.forEach((startup) => {
              if (startup?.center_id) {
                collectedIds.add(startup.center_id);
              }
            });
          } catch (startupFetchError) {
            console.error("Failed to fetch startup centers:", startupFetchError);
          }
        }
      }
 
      if (collectedIds.size > 0) {
        localStorage.setItem("centerIds", JSON.stringify(Array.from(collectedIds)));
      } else {
        localStorage.removeItem("centerIds");
      }
    } catch (err) {
      console.error("persistStartupCenterIds error:", err);
    }
  };
  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Handle MFA verification
  const handleMfaVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${API_BASE_URL}/auth/mfa/login/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          temp_token: tempToken, 
          code: mfaCode 
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        localStorage.setItem("token", data.token);
        localStorage.setItem("userEmail", email);
        localStorage.setItem("roleId", data.role_id);
        localStorage.setItem("roleName", data.role_name);
        localStorage.setItem("centerId", data.center_id);
        await persistStartupCenterIds(data);
        window.dispatchEvent(new Event("login"));

        navigateAfterLogin(data, navigate, {
          toast,
          toastId: "mfa-success",
          successMessage: "Identity verified! Logging you in...",
        });
      } else {
        const msg = data.message || "Verification failed. Please try again.";
        setError(msg);
        toast.error(msg);
      }
    } catch (err) {
      console.error("MFA verification error:", err);
      setError("Server error. Please try again later.");
      toast.error("Server error. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password, remember_me: remember }),
      credentials: "include"   // 🔑 required for cookies
    });


      const data = await response.json();

      if (response.ok && data.success) {
        // Check if MFA is required
        if (data.mfa_required) {
          setTempToken(data.temp_token);
          setShowMfaVerification(true);
          setError(""); // Clear any previous errors
          return;
        }

        // Normal login without MFA
        localStorage.setItem("token", data.token);
        localStorage.setItem("userEmail", email);
        localStorage.setItem("roleId", data.role_id);
        localStorage.setItem("roleName", data.role_name);
        localStorage.setItem("sessionType", data.session_type);
        localStorage.setItem("rememberMe", data.remember_me);
        localStorage.setItem("centerId", data.center_id);
        await persistStartupCenterIds(data);
        window.dispatchEvent(new Event("login"));
 
        navigateAfterLogin(data, navigate, {
          toast,
          toastId: "login-success",
          successMessage: "Login successful! Welcome back.",
        });
      } else {
        const msg = data.message || "Login failed. Please try again.";
        // const msg = response.status === 401 ? "Invalid email or password" : (data.message || "Login failed. Please try again.");
        setError(msg);
        toast.error(msg);
      }
    } catch (err) {
      console.error("Login error:", err);
      setError("Server error. Please try again later.");
      toast.error("Server error. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const FRAUNCES = "'Fraunces', Georgia, serif";

  return (
    <div className="min-h-screen w-full" style={{ background: 'var(--an-white)' }}>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />

      {/* Scoped focus + hover styles for the inputs and coral CTA. The wrapper
          owns the focus ring because the <input> is borderless. */}
      <style>{`
        .an-login-input:focus-within { box-shadow: 0 0 0 2px rgba(255,122,102,.45); }
        .an-login-cta { transition: transform .2s, box-shadow .2s; }
        .an-login-cta:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 12px 28px -12px rgba(255,122,102,.8); }
        .an-login-ghost { transition: background-color .15s, color .15s; }
        .an-login-ghost:hover { background: var(--an-ink); color: #fff; }
        .an-login-back:hover { background: rgba(20,20,30,.04); }
        /* Chrome / Safari autofill — paint the field with our mist tone
           instead of the browser's light-blue/yellow wash. The 1000px
           inset shadow is the standard workaround (background-color is
           not directly overridable on -webkit-autofill). */
        .an-login-input input:-webkit-autofill,
        .an-login-input input:-webkit-autofill:hover,
        .an-login-input input:-webkit-autofill:focus,
        .an-login-input input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 1000px var(--an-mist) inset !important;
          box-shadow: 0 0 0 1000px var(--an-mist) inset !important;
          -webkit-text-fill-color: var(--an-ink) !important;
          caret-color: var(--an-ink) !important;
          transition: background-color 9999s ease-in-out 0s;
        }
      `}</style>

      {/* Cap the overall layout on ultrawide monitors so the dark panel
          doesn't leave huge bands of empty space. */}
      <div className="min-h-screen flex flex-col lg:flex-row lg:max-w-[1400px] mx-auto">

        {/* ─── LEFT — dark brand panel ─────────────────────────────────── */}
        <div
          className="hidden lg:flex lg:flex-1 lg:basis-1/2 relative overflow-hidden flex-col justify-between p-10 xl:p-14 text-white min-h-screen"
          style={{ background: 'var(--an-ink)' }}
        >
          {/* Wordmark — anab + lift-off-l + r */}
          <div
            className="relative z-10 flex items-baseline text-3xl xl:text-4xl font-semibold"
            style={{ letterSpacing: '-0.02em' }}
          >
            anab
            <svg className="inline-block h-7 xl:h-8 w-auto -mb-0.5 mx-0.5" viewBox="0 0 34 100" aria-hidden="true">
              <path d="M10 93 L22 24 L30 12" fill="none" stroke="var(--an-coral)" strokeWidth="11" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            r
          </div>

          {/* Pitch — Fraunces display */}
          <div className="relative z-10">
            <div
              style={{
                fontFamily: FRAUNCES,
                fontWeight: 600,
                fontSize: 54,
                letterSpacing: '-0.015em',
                lineHeight: 1.02,
                textWrap: 'balance',
                maxWidth: 440,
              }}
            >
              The <span style={{ color: 'var(--an-coral)' }}>OS</span> for the startup ecosystem.
            </div>
            <div
              style={{
                color: 'var(--an-silver)',
                fontSize: 16,
                marginTop: 22,
                lineHeight: 1.6,
                maxWidth: '30rem',
              }}
            >
              Run startups, mentors, sessions, events, grants, and reporting from one place. Built for incubators, accelerators, and venture studios.
            </div>
          </div>

          {/* Footer */}
          <div className="relative z-10" style={{ fontSize: 12, color: '#83838E' }}>
            © 2026 anablr · Incubator Co-pilot
          </div>

          {/* Decorative big l in the corner */}
          <svg className="absolute -right-12 -bottom-20 w-[320px] h-[440px]" viewBox="0 0 34 100" aria-hidden="true" style={{ opacity: 0.14 }}>
            <path d="M10 93 L22 24 L30 12" fill="none" stroke="var(--an-coral)" strokeWidth="11" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>

        {/* Mobile brand strip */}
        <div
          className="lg:hidden flex items-baseline justify-center text-2xl font-semibold tracking-tight text-white px-6 py-4"
          style={{ background: 'var(--an-ink)' }}
        >
          anab
          <svg className="inline-block h-5 w-auto mx-0.5 -mb-0.5" viewBox="0 0 34 100" aria-hidden="true">
            <path d="M10 93 L22 24 L30 12" fill="none" stroke="var(--an-coral)" strokeWidth="11" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          r
        </div>

        {/* ─── RIGHT — white form panel ────────────────────────────────── */}
        <div
          className="lg:flex-1 lg:basis-1/2 flex flex-col items-center justify-center p-6 sm:p-10 lg:p-12 flex-1 min-h-screen"
          style={{ background: 'var(--an-white)' }}
        >
          <div className="w-full max-w-[420px]">
            {/* Headline — Fraunces display */}
            <h2
              style={{
                fontFamily: FRAUNCES,
                fontWeight: 600,
                fontSize: 38,
                letterSpacing: '-0.01em',
                lineHeight: 1.1,
                color: 'var(--an-ink)',
                marginBottom: 8,
              }}
            >
              {showMfaVerification ? "Verify Your Identity" : "Welcome back"}
            </h2>
            <h3 style={{ fontSize: 15, color: 'var(--an-slate)', marginBottom: 36 }}>
              {showMfaVerification ? "Enter the verification code we sent to your email." : "Sign in to your incubator workspace."}
            </h3>

            {showMfaVerification ? (
              /* ─── MFA Verification Form ─── */
              <form onSubmit={handleMfaVerify} className="space-y-6">
                <div
                  style={{
                    background: 'var(--an-mist)',
                    border: '1px solid var(--an-hair)',
                    borderRadius: 12,
                    padding: 16,
                    marginBottom: 24,
                  }}
                >
                  <p style={{ color: 'var(--an-slate)', fontSize: 14, textAlign: 'center' }}>
                    A verification code has been sent to your email
                  </p>
                  <p style={{ color: 'var(--an-silver)', fontSize: 12, textAlign: 'center', marginTop: 8 }}>
                    Enter 6-digit email code or backup code
                  </p>
                </div>

                {/* MFA Code Input — 6 boxes */}
                <div className="relative">
                  {/* Hidden real input that captures typing */}
                  <input
                    ref={mfaInputRef}
                    type="text"
                    placeholder="Enter code"
                    value={mfaCode}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setMfaCode(value);
                    }}
                    className="absolute inset-0 w-full h-12 opacity-0 focus:opacity-0"
                    inputMode="numeric"
                    pattern="\d*"
                    autoComplete="one-time-code"
                    required
                    maxLength={6}
                  />
                  {/* 6 visual boxes reflecting the MFA code value */}
                  <div className="grid grid-cols-6 gap-2" onClick={() => mfaInputRef.current?.focus()}>
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <div
                        key={i}
                        className="h-12 flex items-center justify-center"
                        style={{
                          background: 'var(--an-mist)',
                          borderRadius: 10,
                          color: 'var(--an-ink)',
                          fontWeight: 600,
                        }}
                      >
                        {mfaCode?.[i] ? (
                          <span style={{ fontSize: 18 }}>{mfaCode[i]}</span>
                        ) : i === Math.min(mfaCode?.length || 0, 5) ? (
                          <span
                            className="animate-pulse"
                            style={{
                              display: 'inline-block',
                              width: 2,
                              height: 20,
                              background: 'var(--an-slate)',
                              borderRadius: 2,
                            }}
                          />
                        ) : (
                          <span style={{ fontSize: 18, color: 'transparent' }}>0</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Helper Text */}
                <div className="text-center mt-3">
                  <p style={{ color: 'var(--an-silver)', fontSize: 12 }}>
                    {mfaCode.length === 6 && "Code ready to verify"}
                    {mfaCode.length > 0 && mfaCode.length < 6 && `Enter ${6 - mfaCode.length} more digit${6 - mfaCode.length > 1 ? 's' : ''}`}
                  </p>
                </div>

                {/* Verify — coral primary */}
                <button
                  type="submit"
                  className="an-login-cta w-full disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none"
                  style={{
                    padding: 15,
                    background: 'var(--an-coral)',
                    color: 'var(--an-on-coral)',
                    border: 'none',
                    borderRadius: 12,
                    fontWeight: 600,
                    fontSize: 15,
                  }}
                  disabled={loading || mfaCode.length < 6}
                >
                  {loading ? (
                    <div className="flex justify-center items-center gap-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2" style={{ borderColor: 'var(--an-on-coral)' }} />
                      <span>Verifying...</span>
                    </div>
                  ) : (
                    "Verify Code"
                  )}
                </button>

                {/* Back to login — hairline ghost */}
                <button
                  type="button"
                  onClick={() => {
                    setShowMfaVerification(false);
                    setMfaCode("");
                    setTempToken("");
                    setError("");
                  }}
                  className="an-login-back w-full flex items-center justify-center gap-2"
                  style={{
                    padding: 14,
                    border: '1.5px solid var(--an-hair)',
                    background: 'transparent',
                    color: 'var(--an-ink)',
                    borderRadius: 12,
                    fontWeight: 600,
                    fontSize: 14,
                  }}
                >
                  <ArrowLeftIcon className="h-4 w-4" />
                  Back to login
                </button>
              </form>
            ) : (
              /* ─── Normal Login Form ─── */
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Email Input — filled mist */}
                <div>
                  <label
                    htmlFor="email"
                    className="block"
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      color: 'var(--an-slate)',
                      marginBottom: 9,
                    }}
                  >
                    Work email
                  </label>
                  <div
                    className="an-login-input flex items-center"
                    style={{
                      gap: 11,
                      background: 'var(--an-mist)',
                      borderRadius: 12,
                      padding: '14px 15px',
                    }}
                  >
                    <EnvelopeIcon className="h-5 w-5 flex-shrink-0" style={{ color: 'var(--an-slate)' }} />
                    <input
                      type="email"
                      id="email"
                      name="email"
                      placeholder="you@company.com"
                      value={email}
                      onChange={handleChange}
                      className="w-full bg-transparent border-0 outline-none"
                      style={{ color: 'var(--an-ink)', fontSize: 15 }}
                      required
                    />
                  </div>
                </div>

                {/* Password Input — filled mist */}
                <div>
                  <label
                    htmlFor="password"
                    className="block"
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      color: 'var(--an-slate)',
                      marginBottom: 9,
                    }}
                  >
                    Password
                  </label>
                  <div
                    className="an-login-input flex items-center"
                    style={{
                      gap: 11,
                      background: 'var(--an-mist)',
                      borderRadius: 12,
                      padding: '14px 15px',
                    }}
                  >
                    <LockClosedIcon className="h-5 w-5 flex-shrink-0" style={{ color: 'var(--an-slate)' }} />
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      name="password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={handleChange}
                      className="w-full bg-transparent border-0 outline-none"
                      style={{ color: 'var(--an-ink)', fontSize: 15 }}
                      required
                    />
                    <button
                      type="button"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      className="flex-shrink-0 hover:opacity-80 focus:outline-none"
                      style={{ background: 'transparent', border: 'none', color: 'var(--an-slate)', cursor: 'pointer' }}
                      onClick={() => setShowPassword((prev) => !prev)}
                    >
                      {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                {/* Options row */}
                <div className="flex justify-between items-center text-sm">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="remember"
                      checked={remember}
                      onChange={handleChange}
                      className="w-4 h-4 rounded"
                      style={{ accentColor: 'var(--an-coral)' }}
                    />
                    <span style={{ color: 'var(--an-graphite)', fontSize: 14, userSelect: 'none' }}>Remember me</span>
                  </label>
                  <Link
                    to="/forgot-password"
                    className="hover:underline transition-colors"
                    style={{ color: 'var(--an-slate)', fontSize: 14 }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--an-ink)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--an-slate)'; }}
                  >
                    Forgot password?
                  </Link>
                </div>

                {/* Inline error — optional. Only shown when `error` is truthy */}
                {error && (
                  <div
                    role="alert"
                    style={{
                      background: 'rgba(196,69,60,.06)',
                      border: '1px solid rgba(196,69,60,.35)',
                      color: 'var(--an-danger)',
                      borderRadius: 12,
                      padding: '12px 14px',
                      fontSize: 14,
                    }}
                  >
                    {error}
                  </div>
                )}

                {/* Submit — coral primary, dark spinner */}
                <button
                  type="submit"
                  className="an-login-cta w-full disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none"
                  style={{
                    padding: 15,
                    background: 'var(--an-coral)',
                    color: 'var(--an-on-coral)',
                    border: 'none',
                    borderRadius: 12,
                    fontWeight: 600,
                    fontSize: 15,
                  }}
                  disabled={loading}
                >
                  {loading ? (
                    <div className="flex justify-center items-center gap-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2" style={{ borderColor: 'var(--an-on-coral)' }} />
                      <span>Signing in...</span>
                    </div>
                  ) : (
                    "Sign in"
                  )}
                </button>

                {/* MFA tagline */}
                <div className="flex items-center justify-center gap-1.5 pt-1" style={{ fontSize: 12, color: 'var(--an-slate)' }}>
                  <LockClosedIcon className="h-3.5 w-3.5" />
                  <span>Protected by multi-factor authentication</span>
                </div>
              </form>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;